import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MyPositionType, PositionStatus, UserRole } from 'types';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useUserContext } from 'src/context/UserProvider';
import { TbSearch } from 'react-icons/tb';
import { Badge, Pagination, Spinner, TextInput } from 'flowbite-react';
import classNames from 'classnames';
import { useRecommendationsContext } from 'src/context/RecommendationsProvider';
import { Loader } from 'ui';
import { debounce, filter } from 'lodash';
import { useMutation } from 'react-query';
import { getMyRegisteredPositions } from 'src/apis/position';
import { revertPositionTitle } from 'shared-utils';
import dayjs from 'dayjs';
import PositionsTopBar from 'src/components/recruiterPositions/RecruiterPositionsTopBar';
import RecruiterPositionsTabsComponent from 'src/components/recruiterPositions/RecruiterPositionsTabsComponent';
import RecruiterPositionCard from 'src/components/recruiterPositions/RecruiterPositionCard';
import NoRecruiterPositionsContainer from 'src/components/recruiterPositions/NoRecruiterPositionsContainer';
import CloseRecruiterPositionConfirmationModal from 'src/components/recruiterPositions/CloseRecruiterPositionConfirmationModal';
import ReopenPositionConfirmationModal from 'src/components/recruiterPositions/ReopenPositionConfirmationModal';
import CloseRecruiterPositionModal from 'src/components/recruiterPositions/CloseRecruiterPositionModal';
import InviteCollaborator from 'src/components/position/InviteCollaborator';
import RecruiterPositionDrawer from 'src/components/recruiterPositions/RecruiterPositionDrawer';
import UpdatePositionExpiryModal from 'src/components/position/UpdatePositionExpiryModal';
import NewCreatingPositionLoader from 'src/components/position/NewCreatingPositionLoader';
import { all } from 'axios';
import { useMediaQuery } from '@mui/material';
import PositionsTabsComponent from 'src/components/position/PositionsTabsComponent';

interface Props {
    positionsData: MyPositionType[];
    AllpositionsCount: number;
    allActivePositionsCount: number;
    allClosedPositionsCount: number;
    myPositionsCount: number;
    myActivePositionsCount: number;
    myClosedPositionsCount: number;
    handleCreatePosition: () => void;
    disableCreate: boolean;
    totalPositions: number;
}

const NewRecruiterPositionsContainer = ({
    positionsData,
    AllpositionsCount,
    myPositionsCount,
    allActivePositionsCount,
    allClosedPositionsCount,
    myActivePositionsCount,
    myClosedPositionsCount,
    handleCreatePosition,
    totalPositions,
    disableCreate }: Props) => {

    const limit = 6;
    const isMobile = useMediaQuery('(max-width:960px)');

    const navigate = useNavigate();
    const { companyId } = useParams();
    const { isAccountManagerLoggedIn, isRecruiterLoggedIn, user } = useUserContext();
    const { setFilterApplied, isFilterLoading, selectedCompany, setSelectedCompany } = useRecommendationsContext();
    const [isPositionDrawerOpen, setIsPositionDrawerOpen] = useState<boolean>(false);
    const [hasUnpulishedPositions, setHasUnpulishedPositions] = useState<boolean>(false);
    const [positionId, setPositionId] = useState<string>();
    const [openAddCollaboratorModal, setOpenAddCollaboratorModal] = useState(false);
    const [filteredPositions, setFilteredPositions] = useState<MyPositionType[]>([]);
    const [openPositionUpdateExpiryModal, setOpenPositionUpdateExpiryModal] = useState(false);
    const [isCloseRecruiterPositionConfirmationModalOpen, setIsCloseRecruiterPositionConfirmationModalOpen] =
        useState<boolean>(false);
    const [isReopenPositionModalOpen, setIsReopenPositionModalOpen] = useState<boolean>(false);
    const [isCloseRecruiterPositionModal, setIsCloseRecruiterPositionModal] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const [searchParams, setSearchParams] = useSearchParams();

    let tab = searchParams.get('allPosition') === 'true' || !searchParams.has('myPosition') ? "allPosition" : "myPosition";
	let allPosition = searchParams.get('allPosition') === 'true' || !searchParams.has('myPosition');
	let myPosition = searchParams.get('myPosition') === 'true';
	const [active, setActive] = useState<{ open: boolean; closed: boolean }>({
		open: true,
		closed: false
	});

    const toggleActive = () => {
        setActive(prevState => ({ open: !prevState.open, closed: !prevState.closed }));
    };

    const allPages = useMemo(() => {
        if (allPosition) {
            return searchParams.get('positionStatus') === "Open" || active.open ? Math.ceil(allActivePositionsCount / limit) : Math.ceil(allClosedPositionsCount / limit);
        } else if (myPosition) {
            return searchParams.get('positionStatus') === "Open" || active.open ? Math.ceil(myActivePositionsCount / limit) : Math.ceil(myClosedPositionsCount / limit);
        }
        return 1;
    }, [allPosition, myPosition, active, allActivePositionsCount, allClosedPositionsCount, myActivePositionsCount, myClosedPositionsCount, limit]);

    console.log('active', active);

    const handleViewPosition = (positionId: string) => {
        setPositionId(positionId);
        setIsPositionDrawerOpen(true);
    };
    const getMyRegisteredPositionsFn = useMutation(['getMyRegisteredPositions', companyId], getMyRegisteredPositions, {
        onMutate: () => {
            setIsLoading(true);
        },
        onSuccess(data) {
            setFilteredPositions(data?.positions ?? []);
            setIsLoading(false);
        },
        onError: () => {
            setIsLoading(false);
        }
    });

    const changeHandler = ({ search, allPosition, positionStatus, myPosition, page }: {
        search?: string;
        allPosition?: boolean;
        positionStatus?: string;
        myPosition?: boolean;
        page?: number;
        limit?: number;

    }) => {
        if (search) {
            search = revertPositionTitle(search?.toLowerCase()?.trim());
        }

        if (isRecruiterLoggedIn || companyId?.length) getMyRegisteredPositionsFn.mutate({
            companyId,
            name: search,
            allPosition: allPosition,
            positionStatus: positionStatus,
            myPosition: myPosition,
            page: page || 1,
            limit: limit
        });
    };

    const debouncedChangeHandler = useCallback(debounce(changeHandler, 1000), []);

    useEffect(() => {
        const filterParams: {
            search: string;
            allPosition?: boolean;
            positionStatus: string;
            myPosition?: boolean;
            page: number;
            limit: number;
        } = {
            search: searchParams.get('search') || '',
            positionStatus: searchParams.get('positionStatus') || PositionStatus.OPEN,
            page: +(searchParams.get('page') || '1'),
            limit: limit
        };

        // Conditionally add `allPosition` or `myPosition` based on their values
        const allPosition = searchParams.get('allPosition') === 'true' || !searchParams.has('myPosition');
        const myPosition = searchParams.get('myPosition') === 'true';

        if (allPosition) {
            filterParams.allPosition = true;
        } else if (myPosition) {
            filterParams.myPosition = true;
        }

        console.log('filterParams', filterParams);

        debouncedChangeHandler(filterParams);
    }, [debouncedChangeHandler, searchParams]);

    const handleSearchInput = (value: string) => {
        const newParams = new URLSearchParams(searchParams);
        newParams.set("search", value);
        setSearchParams(newParams);
    }
    const onChange = (value: string) => {

		if (value === "allPosition") {
			if (active?.open) {
				setSearchParams({ allPosition: 'true', positionStatus: PositionStatus.OPEN });
			}
			if (active?.closed) {
				setSearchParams({ allPosition: 'true', positionStatus: PositionStatus.CLOSED });

			};
			tab=value;

		}

		if (value === "myPosition") {
			if (active?.open) {
				setSearchParams({  positionStatus: PositionStatus.OPEN, myPosition: 'true' });
			}
			if (active?.closed) {
				setSearchParams({ positionStatus: PositionStatus.CLOSED, myPosition: 'true' });
			};
			tab=value;

		}

	};
    return (
        <>
            <PositionsTopBar
                companyName={
                    selectedCompany?.company?.name ||
                    (positionsData?.length > 0 ? positionsData[0]?.position?.company?.name : undefined)
                }
                handleCreatePosition={handleCreatePosition}
                disableCreate={disableCreate}
                title="Positions"
                buttonText="Create new"
            />

            <div className="mt-4">
                <div className={classNames('flex mb-[10px] gap-2', 'flex-col')}>
                    <div className="w-full flex flex-wrap items-center justify-between gap-[10px]">
                        {/* Top Tabs for recommended and connected */}

                        <PositionsTabsComponent
                            tab={tab}
                            AllpositionsCount={AllpositionsCount}
                            myPositionsCount={myPositionsCount}
                            onChange={onChange}
                        />

                        <div className="w-full md:mt-0 md:max-w-[522px] flex items-center gap-[12px] justify-stretch">
                            {/* Search Input */}
                            <TextInput
                                icon={() => <TbSearch color="#C7C9D9" />}
                                className="w-full [&_input]:bg-white"
                                placeholder="Search Position"
                                name="search"
                                value={searchParams.get('search') || ''}
                                rightIcon={() => (isLoading && searchParams.get('search') ? <Spinner /> : null)}
                                onChange={e => handleSearchInput(e.target.value)}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className='mt-8 px-2 flex justify-between'>
					{allPosition &&
						<div className='flex flex-row gap-[20px] rounded-md'>
							<button
								className={classNames('', {
									'text-blue-500':
										(searchParams.get('positionStatus') === PositionStatus.OPEN && searchParams.get('allPosition') === 'true') || (active.open && searchParams.get('positionStatus') !== PositionStatus.CLOSED)

								})}
								onClick={() => {
									toggleActive();
									setSearchParams({ allPosition: 'true', positionStatus: PositionStatus.OPEN });
								}}
								disabled={(searchParams.get('allPosition') === 'true' && searchParams.get('positionStatus') === PositionStatus.OPEN) || isLoading}
							>
								Active ({allActivePositionsCount})
							</button>
							<button
								className={classNames('', { 'text-blue-500': searchParams.get('positionStatus') === PositionStatus.CLOSED && !active.open || searchParams.get('positionStatus') === PositionStatus.CLOSED })}
								onClick={() => {
									toggleActive();
									setSearchParams({ allPosition: 'true', positionStatus: PositionStatus.CLOSED });

								}}
								disabled={(searchParams.get('allPosition') === 'true' && searchParams.get('positionStatus') === PositionStatus.CLOSED) || isLoading}
							>
								Closed ({allClosedPositionsCount})
							</button>
						</div>
					}

					{myPosition &&
						<div className='flex flex-row gap-[20px] rounded-md'>
							<button className={classNames('', { 'text-blue-500': searchParams.get('positionStatus') === PositionStatus.OPEN })}
								onClick={() => {
									toggleActive();
									setSearchParams({ myPosition: 'true', positionStatus: PositionStatus.OPEN });

								}}
								disabled={(searchParams.get('myPosition') === 'true' && searchParams.get('positionStatus') === PositionStatus.OPEN) || isLoading}
							>Active ({myActivePositionsCount})</button>
							<button className={classNames('', { 'text-blue-500': searchParams.get('positionStatus') === PositionStatus.CLOSED })}
								onClick={() => {
									toggleActive();
									setSearchParams({ myPosition: 'true', positionStatus: PositionStatus.CLOSED });

								}}
								disabled={(searchParams.get('myPosition') === 'true' && searchParams.get('positionStatus') === PositionStatus.CLOSED) || isLoading}
							>Closed ({myClosedPositionsCount})</button>
						</div>
					}

                    {filteredPositions?.length ?  (
                        <div>
                            <Pagination
                                showIcons
                                color="purplePlaced"
                                className="-mt-2"
                                layout={isMobile ? 'navigation' : 'pagination'}
                                currentPage={+(searchParams.get('page') || 1)}
                                totalPages={allPages}
                                onPageChange={(page) => {
                                    console.log('page', page);
                                    searchParams.set('page', String(page || 1));
                                    setSearchParams(searchParams);
                                }}
                            />
                        </div>
                    ):null}
				</div>
            <hr className='border border-grey-500 mt-2' />

            <div className="flex justify-center my-5">
                <div className="flex flex-wrap gap-7 sm:flex-col w-full sm:gap-4">

                    {isLoading ? (
                        <div className="w-full h-full flex items-center justify-center">
                            <NewCreatingPositionLoader showAnimation={true} progressBar={false} customClassCss="!w-[30%] mx-auto " />
                        </div>
                    ) : filteredPositions.length ? (
                        filteredPositions.map((p, i) => (
                            <div key={i} className="sm:w-full md:min-w-[352px] md:w-[calc(33.3%-19px)] relative isolate">
                                <RecruiterPositionCard
                                    positionObj={p}
                                    optionalData={[
                                        { title: 'Shortlisted', data: p?.recommendedUsers + p?.connectedUsers },
                                        { title: 'Matched', data: p?.connectedUsers + p?.inPipeLineUsers },
                                        { title: 'In Pipeline', data: p?.inPipeLineUsers },
                                    ]}
                                    handleViewRecommendationsTrigger={() => {
                                        if (tab === PositionStatus.OPEN || user?.allowedRoles?.includes(UserRole.SUPER_ADMIN) || myPosition) {
                                            navigate(`/view-candidates/position/${p.position?._id}`);
                                        }
                                    }}
                                    handleClosePosition={() => {
                                        setPositionId(String(p.position?._id));
                                        setIsCloseRecruiterPositionConfirmationModalOpen(true);
                                    }}
                                    handleReopenPosition={() => {
                                        setPositionId(String(p.position?._id));
                                        setIsReopenPositionModalOpen(true);
                                    }}
                                    handleAddCollaborator={() => {
                                        setPositionId(String(p.position?._id));
                                        setOpenAddCollaboratorModal(true);
                                    }}
                                    handleUpdatePositionExpiry={() => {
                                        setPositionId(String(p.position?._id));
                                        setOpenPositionUpdateExpiryModal(true);
                                    }}
                                    handleViewPosition={handleViewPosition}
                                />
                            </div>
                        ))
                    ) : (
                        <div className=" mt-4 w-full h-full flex flex-col justify-center items-center">
                            <div className="min-h-[350px] flex flex-col items-center justify-center">
                                <NoRecruiterPositionsContainer
                                    title={tab === PositionStatus.OPEN ? 'No active job postings' : 'No closed job postings!'}
                                    description=""
                                    showAction={false}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {filteredPositions?.length ?  (
            <div className='hidden md:flex justify-center items-center'>
                <Pagination
                    showIcons
                    color="purplePlaced"
                    className="-mt-2"
                    layout={isMobile ? 'navigation' : 'pagination'}
                    currentPage={+(searchParams.get('page') || 1)}
                    totalPages={allPages}
                    onPageChange={page => {
                        console.log('page', page);
                        searchParams.set('page', String(page || 1));
                        setSearchParams(searchParams);
                    }}
                />
            </div>
             ):null}

            <CloseRecruiterPositionConfirmationModal
                isOpen={isCloseRecruiterPositionConfirmationModalOpen}
                onClose={() => setIsCloseRecruiterPositionConfirmationModalOpen(false)}
                onConfirm={() => {
                    setIsCloseRecruiterPositionConfirmationModalOpen(false);
                    setIsCloseRecruiterPositionModal(true);
                }}
            />
            <ReopenPositionConfirmationModal
                isOpen={isReopenPositionModalOpen}
                positionId={positionId || ''}
                onClose={() => setIsReopenPositionModalOpen(false)}
            />
            <CloseRecruiterPositionModal
                positionId={positionId || ''}
                isOpen={isCloseRecruiterPositionModal}
                onClose={() => setIsCloseRecruiterPositionModal(false)}
            />

            <InviteCollaborator
                positionId={positionId || ''}
                isOpen={openAddCollaboratorModal}
                onClose={() => setOpenAddCollaboratorModal(false)}
            />

            <RecruiterPositionDrawer
                isPositionDrawerOpen={isPositionDrawerOpen}
                positionId={positionId || ''}
                setIsPositionDrawerOpen={setIsPositionDrawerOpen}
                closeDrawer={() => setIsPositionDrawerOpen(false)}
            />

            <UpdatePositionExpiryModal
                positionId={positionId || ''}
                currentExpiryDate={positionsData?.find(p => p.position?._id === positionId)?.position?.expiresAt || new Date()}
                isOpen={openPositionUpdateExpiryModal}
                onClose={() => setOpenPositionUpdateExpiryModal(false)}
            />
        </>
    );
};

export default NewRecruiterPositionsContainer;
